/**
 * NMS Buy Now Component
 * Handles direct buy now functionality without modal
 * Integrates with GoKwik onBuyNowClick function
 * @extends NMSComponent
 */

// Define base NMS Component class if not already defined
window.NMSComponent =
  window.NMSComponent ||
  class {
    constructor(element) {
      this.element = element;
    }

    init() {}

    dispatchCustomEvent(eventName, detail = {}) {
      const event = new CustomEvent(`nms:${eventName}`, {
        detail: {
          component: this,
          element: this.element,
          ...detail,
          timestamp: Date.now(),
        },
        bubbles: true,
        cancelable: true,
      });

      this.element.dispatchEvent(event);
    }

    destroy() {}
  };

// Ensure NMS namespace exists
window.neemans = window.neemans || {};

/**
 * NMS Buy Now Component
 */
class NMSBuyNow extends NMSComponent {
  static componentName = 'nms-buy-now';

  constructor(element) {
    super(element);

    // Store component data from element
    this.formId = element.dataset.formId;

    // Cache DOM elements
    this.form = null;
    this.variantInput = null;
    this.quantityInput = null;

    // Bind methods
    this.handleBuyNowClick = this.handleBuyNowClick.bind(this);

    this.init();
  }

  init() {
    this.setupRefs();
    this.bindEvents();
  }

  setupRefs() {
    // Get the associated form
    this.form = document.getElementById(this.formId);
    if (!this.form) {
      console.error(`NMSBuyNow: Form with ID "${this.formId}" not found`);
      return;
    }

    // Get variant input from form
    this.variantInput = this.form.querySelector('input[name="id"]');
    this.quantityInput =
      this.form.querySelector('input[name="quantity"]') || document.querySelector('input[name="quantity"]');
  }

  bindEvents() {
    // Bind click event to buy button
    this.element.addEventListener('click', this.handleBuyNowClick);
  }

  /**
   * Handle buy now button click
   * Gets selected variant and calls GoKwik onBuyNowClick function
   */
  handleBuyNowClick(event) {
    event.preventDefault();

    try {
      // Get selected variant ID
      const variantId = this.getSelectedVariantId();
      if (!variantId) {
        console.error('NMSBuyNow: No variant selected');
        return;
      }

      // Get quantity
      const quantity = this.getQuantity();

      // Call GoKwik buy now function
      this.callBuyNowFunction(event, variantId, quantity);
    } catch (error) {
      console.error('NMSBuyNow: Failed to process buy now:', error);
    }
  }

  /**
   * Get currently selected variant ID from form
   */
  getSelectedVariantId() {
    if (this.variantInput && this.variantInput.value) {
      return this.variantInput.value;
    }

    // Fallback: try to get from Shopify analytics
    if (window.ShopifyAnalytics?.meta?.selectedVariantId) {
      return window.ShopifyAnalytics.meta.selectedVariantId;
    }

    // Fallback: get first available variant from window.neemans data
    const productData = window.neemans?.products;
    if (productData) {
      for (const product of Object.values(productData)) {
        if (product.variants && product.variants.length > 0) {
          const availableVariant = product.variants.find(v => v.available);
          if (availableVariant) {
            return availableVariant.id;
          }
        }
      }
    }

    return null;
  }

  /**
   * Get quantity from input or default to 1
   */
  getQuantity() {
    if (this.quantityInput && this.quantityInput.value) {
      return parseInt(this.quantityInput.value, 10) || 1;
    }
    return 1;
  }

  /**
   * Call GoKwik onBuyNowClick function
   */
  callBuyNowFunction(event, variantId, quantity) {
    // Wait for GoKwik to be loaded
    if (typeof onBuyNowClick !== 'function') {
      console.log('NMSBuyNow: Waiting for GoKwik onBuyNowClick function...');
      setTimeout(() => {
        if (typeof onBuyNowClick === 'function') {
          this.callBuyNowFunction(event, variantId, quantity);
        } else {
          console.error('NMSBuyNow: onBuyNowClick function not available');
        }
      }, 100);
      return;
    }

    try {
      // Get GST properties from the product form
      const properties = this.getGSTProperties();
      
      // Call GoKwik function with selected variant and properties
      console.log('NMSBuyNow: Calling onBuyNowClick with variant:', variantId, 'quantity:', quantity, 'properties:', properties);
      onBuyNowClick(event, false, [{ id: variantId, quantity: quantity, properties: properties }]);

      // Dispatch custom event for tracking
      this.dispatchCustomEvent('buy-now-clicked', {
        variantId: variantId,
        quantity: quantity,
        properties: properties,
      });
    } catch (error) {
      console.error('NMSBuyNow: Error calling onBuyNowClick:', error);
    }
  }

  /**
   * Get GST properties from the product form
   */
  getGSTProperties() {
    const properties = {};
    
    // Find the product form (could be in the same form or a related form)
    const productForm = this.element.closest('product-form') || document.querySelector('product-form');
    console.log('🔍 NMSBuyNow: Product form found:', productForm);
    
    if (productForm) {
      // Get GST rate reduction value
      const gstRateInput = productForm.querySelector('input[name="properties[gst_rate_reduction]"]');
      console.log('💰 NMSBuyNow: GST rate input found:', gstRateInput, 'value:', gstRateInput?.value);
      if (gstRateInput && gstRateInput.value) {
        properties.gst_rate_reduction = gstRateInput.value;
      }
      
      // Get original price (compare_at_price)
      const originalPriceInput = productForm.querySelector('input[name="properties[original_price]"]');
      console.log('💲 NMSBuyNow: Original price input found:', originalPriceInput, 'value:', originalPriceInput?.value);
      if (originalPriceInput && originalPriceInput.value) {
        properties.original_price = originalPriceInput.value;
      }
    }
    
    console.log('📦 NMSBuyNow: Final properties object:', properties);
    return properties;
  }

  /**
   * Public API method to trigger buy now programmatically
   */
  triggerBuyNow(variantId = null, quantity = null) {
    const selectedVariantId = variantId || this.getSelectedVariantId();
    const selectedQuantity = quantity || this.getQuantity();

    if (!selectedVariantId) {
      console.error('NMSBuyNow: No variant ID provided');
      return false;
    }

    const mockEvent = new Event('click');
    this.callBuyNowFunction(mockEvent, selectedVariantId, selectedQuantity);
    return true;
  }

  destroy() {
    // Remove event listeners
    this.element.removeEventListener('click', this.handleBuyNowClick);

    super.destroy();
  }
}

// Register component
window.neemans.NMSBuyNow = NMSBuyNow;

/**
 * Auto-initialization for buy now components
 */
const initBuyNowComponents = () => {
  const components = document.querySelectorAll('[data-nms-buy-now]:not([data-buy-now-initialized])');
  components.forEach(component => {
    new NMSBuyNow(component);
    component.dataset.buyNowInitialized = 'true';
  });
};

// Initialize on DOM ready or immediately if already loaded
document.readyState === 'loading'
  ? document.addEventListener('DOMContentLoaded', initBuyNowComponents)
  : initBuyNowComponents();

// Re-initialize on dynamic content changes
document.addEventListener('shopify:section:load', initBuyNowComponents);

// Listen for color content updates and re-initialize within the updated container
document.addEventListener('nms:color-content-updated', event => {
  const { container, source } = event.detail;

  const components = container.querySelectorAll('[data-nms-buy-now]:not([data-buy-now-initialized])');
  components.forEach(component => {
    new NMSBuyNow(component);
    component.dataset.buyNowInitialized = 'true';
  });
});

// Listen for variant changes to update button state
document.addEventListener('variant:change', event => {
  console.log('NMSBuyNow: Variant changed, updating button states');
  // Re-initialize components to pick up new variant data
  initBuyNowComponents();
});
